import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
} from "@angular/core";
import { ComplaintPdfService } from "../complaint-pdf.service";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatSortModule } from "@angular/material/sort";
import { MatDialog } from "@angular/material/dialog";
import { GenericCrudService } from "../../../core/services/generic-crud.service";
import { MatTableModule } from "@angular/material/table";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { TranslateModule } from "@ngx-translate/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { MatOption } from "@angular/material/autocomplete";
import { MatSelect } from "@angular/material/select";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors
} from "@angular/forms";
import { Config } from "../../../core/DefaultConfig";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { provideNativeDateAdapter } from "@angular/material/core";
import { SendSmsDialogComponent } from "./send-sms-dialog/send-sms-dialog.component";
import { ApplicationDataService } from "../../../core/services/application-data.service";
import { AuthenticationService } from "../../../core/services/authentication.service";
import { AlertDialogComponent } from "../../../shared/alert-dialog/alert-dialog.component";
import { DatePipe, NgClass } from "@angular/common";
import { HttpHeaders, HttpClient } from "@angular/common/http";
import { AppUrlConstants } from "../../../core/constants/AppUrlConstants";
import { SendEmailDialogComponent } from "./send-email-dialog/send-email-dialog.component";
import { DropDialogComponent } from "./drop-dialog/drop-dialog.component";
import { AppealDialogComponent } from "./appeal-dialog/appeal-dialog.component";
import { ReopenDialogComponent } from "./reopen-dialog/reopen-dialog.component";
import { AttachmentPreviewDialogComponent } from "./attachment-preview-dialog/attachment-preview-dialog.component";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { TokenStorageService } from "../../../core/services/token-storage.service";
import { SpinnerService } from "../../../core/services/spinner.service";
import { NgxEditorModule } from "ngx-editor";
import { Editor, Toolbar } from "ngx-editor";
import "./NotoNastaliqUrdu-Regular-normal";
import { Plugin, PluginKey } from 'prosemirror-state';

interface Complaint {
  complaintId?: string;
  complaintNo?: string;
  complainantTitle?: string;
  complainantName?: string;
  cnic?: string;
  cellPhoneNo?: string;
  passportNo?: string;
  landLinePhoneNo?: string;
  address?: string;
  complaintCategoryName?: string;
  complaintSubcategoryName?: string;
  divisionName?: string;
  districtName?: string;
  tehsilName?: string;
  tehsilId?: string;
  mouzaId?: string;
  mouzaName?: string;
  daysRequired?: string;
  workCode?: string;
  complaintSource?: string;
  legalFramework?: string;
  helplineIdentifier?: string;
  assignTo?: string;
  complaintDocument?: any[];
  //description?: string;
  complaintDetails?: string;
  priority?: string;
  severity?: string;
  gender?: string;
  email?: string;
  assignToGroup?: string;
  complaintAddress?: string;
  currentProcess?: {
    complaintProcessId?: string;
    name?: string;
    successorProcessId?: any[];
  };
  isAnonymous?: boolean;
  expectedResolutionDate?: string;
  segmentId?: number;
  otherForum?: string;
  otherSource?: string;
  divisionId?: string;
  districtId?: string;
  assignToName?: string;
}

interface TrackingItem {
  createdByName?: string;
  createdAt?: string;
  comment?: string;
  statusChangedName?: string;
  priorityChanged?: string;
  severityChanged?: string;
  documentName?: string;
  isRemove?: boolean | null;
  complaintEmailMessage?: string;
  complaintSMSMessage?: string;
  assignedToName?: string;
  assignedToGroupName?: string;
}

interface RegionObject {
  divisionsId?: string;
  districtsId?: string;
  tehsilId?: string;
  mouzaId?: string;
}

interface AdminStructure {
  adminStrId: string;
  name: string;
  parentAdminStrId?: string;
}

interface User {
  segmentId?: number;
  [key: string]: any;
}

interface Group {
  groupId: string;
  name: string;
  [key: string]: any;
}

interface SendMessageModel {
  title: string;
  sendToLabel: string;
  sendTo: string;
  message: string;
  complaintId: string;
}

// Validator Logic
const complaintStatusValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const status = control.get('currentProcessName')?.value?.toLowerCase();
  const restrictedStatuses = ["closed", "appeal closed", "drop"];
  return restrictedStatuses.includes(status) ? { notValidStatus: true } : null;
};

@Component({
  selector: "app-complaint-edit",
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatGridListModule,
    TranslateModule,
    RouterModule,
    MatOption,
    MatSelect,
    ReactiveFormsModule,
    MatDatepickerModule,
    DatePipe,
    MatCheckboxModule,
    FormsModule,
    NgxEditorModule,
    NgClass,
    MatTooltipModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: "./complaint-edit.component.html",
  styleUrl: "./complaint-edit.component.css",
})
export class ComplaintEditComponent implements OnInit, OnDestroy {
  @ViewChild("content") content!: ElementRef;
  @ViewChild("searchMouza") searchMouza!: ElementRef;

  id: any;
  form!: FormGroup;
  userId: string | null = null;
  complaintId: string | null = null;
  isVerifying: boolean = true;
  userRole: string;
  username: string = '';
  complaint?: Complaint;
  trackingList: TrackingItem[] = [];
  documentList: any[] = [];
  complaintProcessList: any[] = [];
  complaintProcessSuccessorList: any[] = [];
  groupsList: Group[] = [];
  usersList: User[] = [];
  usersListOriginal: User[] = [];
  mouzaList: AdminStructure[] = [];
  allMouzaList: AdminStructure[] = [];
  originalMouzaList: AdminStructure[] = [];
  isMouzaLoading: boolean = false;
  mouzaIds: string[] = [];
  canSeeInfo: boolean = false;
  canViewAnonymousInfo: boolean = false;
  fileToUpload: File | null = null;
  fileError: boolean = false;
  editor!: Editor;
  commentsEditor!: Editor;

  regionObject: RegionObject = {};

  config = new Config();
  categoryList: any[] = [];
  subCategoryList: any[] = [];
  divisionList: any[] = [];
  districtList: any[] = [];
  tehsilList: any[] = [];
  notoFontBase64: any;

  toolbar: Toolbar = [
    ["bold", "italic"],
    ["underline", "strike"],
    ["blockquote"],
    ["ordered_list", "bullet_list"],
    [{ heading: ["h1", "h2", "h3", "h4", "h5", "h6"] }],
    ["text_color", "background_color"],
    ["align_left", "align_center", "align_right", "align_justify"],
  ];
  commentstoolbar: Toolbar = [
    ["bold", "italic"],
    ["underline", "strike"],
    ["blockquote"],
    [{ heading: ["h1", "h2", "h3", "h4", "h5", "h6"] }],
    ["text_color", "background_color"],
    ["align_left", "align_center", "align_right", "align_justify"],
  ];
  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private appDataService: ApplicationDataService,
    public tokenService: TokenStorageService,
    private authService: AuthenticationService,
    private crudService: GenericCrudService,
    private dialog: MatDialog,
    private http: HttpClient,
    private pdfService: ComplaintPdfService,
    private spinnerService: SpinnerService
  ) {
    this.mouzaIds = this.tokenService.getMouza();
  }

  get f() {
    return this.form.controls;
  }

  get attachments() {
    return this.form.controls["attachments"] as FormArray;
  }

  ngOnInit() {
    this.spinnerService.suppress();
    this.editor = new Editor({
      content: '',
      plugins: [
        new Plugin({
          key: new PluginKey('blockImagesAndSanitizePaste'),
          props: {
            handlePaste(view, event) {
              if (event.clipboardData) {
                const types = event.clipboardData.types;

                // Prevent image paste
                if (types.includes('Files')) {
                  return true;
                }

                // GET PLAIN TEXT ONLY - Ye formatting ko remove karega
                const plainText = event.clipboardData.getData('text/plain');

                if (plainText) {
                  event.preventDefault();

                  // Insert plain text without any formatting
                  const { state, dispatch } = view;
                  const { tr, selection } = state;

                  // Insert text at current cursor position
                  const transaction = tr.insertText(plainText, selection.from, selection.to);
                  dispatch(transaction);

                  return true; // Prevent default paste behavior
                }
              }
              return false;
            },

            handleDrop(view, event) {
              if (event.dataTransfer) {
                // Prevent image drop
                if (event.dataTransfer.files.length > 0) {
                  return true;
                }

                // OPTIONAL: Handle text drop bhi plain text ki tarah
                const plainText = event.dataTransfer.getData('text/plain');
                if (plainText) {
                  event.preventDefault();

                  const { state, dispatch } = view;
                  const pos = view.posAtCoords({
                    left: event.clientX,
                    top: event.clientY
                  });

                  if (pos) {
                    const transaction = state.tr.insertText(plainText, pos.pos);
                    dispatch(transaction);
                  }

                  return true;
                }
              }
              return false;
            },
          }
        })
      ]
    });
    this.commentsEditor = new Editor({
      content: '',
      plugins: [
        new Plugin({
          key: new PluginKey('blockImagesAndSanitizePasteComments'),
          props: {
            handlePaste(view, event) {
              if (event.clipboardData) {
                const types = event.clipboardData.types;

                // Prevent image paste
                if (types.includes('Files')) {
                  return true;
                }

                // GET PLAIN TEXT ONLY - Ye formatting ko remove karega
                const plainText = event.clipboardData.getData('text/plain');

                if (plainText) {
                  event.preventDefault();

                  // Insert plain text without any formatting
                  const { state, dispatch } = view;
                  const { tr, selection } = state;

                  // Insert text at current cursor position
                  const transaction = tr.insertText(plainText, selection.from, selection.to);
                  dispatch(transaction);

                  return true; // Prevent default paste behavior
                }
              }
              return false;
            },

            handleDrop(view, event) {
              if (event.dataTransfer) {
                // Prevent image drop
                if (event.dataTransfer.files.length > 0) {
                  return true;
                }

                // OPTIONAL: Handle text drop bhi plain text ki tarah
                const plainText = event.dataTransfer.getData('text/plain');
                if (plainText) {
                  event.preventDefault();

                  const { state, dispatch } = view;
                  const pos = view.posAtCoords({
                    left: event.clientX,
                    top: event.clientY
                  });

                  if (pos) {
                    const transaction = state.tr.insertText(plainText, pos.pos);
                    dispatch(transaction);
                  }

                  return true;
                }
              }
              return false;
            },
          }
        })
      ]
    });
    this.route.paramMap.subscribe((params) => {
      this.complaintId = params.get("complaintId");
    });
    this.canSeeInfo = this.tokenService.getCanseeInfo();
    this.canViewAnonymousInfo = this.tokenService.getCanViewAnonymousInfo();
    this.username = this.tokenService.getEmail() || '';
    this.loadUserid();
    this.createForm();

    this.getAllWorkFlowStepsBySegment(this.authService.getUserSegment());
    if (this.complaintId) {
      this.appDataService.getComplaintById(this.complaintId).subscribe({
        next: (res: any) => {
          this.complaint = res?.data;

          // Authorization check - nested if-else
          const authStartTime = Date.now();
          const minDelay = 2000; // 2 seconds minimum on verification screen

          const finishVerification = (authorized: boolean) => {
            const elapsed = Date.now() - authStartTime;
            const remaining = Math.max(0, minDelay - elapsed);
            setTimeout(() => {
              if (authorized) {
                this.isVerifying = false;
              } else {
                //this.openAlert('You are not authorized to view this complaint.');
                this.router.navigate(['/complaint']);
              }
            }, remaining);
          };

          if (this.userRole === 'ADMIN') {
            finishVerification(true);
          } else if (this.complaint?.assignToName === this.username) {
            finishVerification(true);
          } else if (this.complaint?.assignToGroup) {
            this.appDataService.getGroupById(Number(this.complaint.assignToGroup)).subscribe({
              next: (groupRes: any) => {
                const groupData = groupRes?.data;
                const isUserInGroup = groupData?.users?.some(
                  (user: any) => user.userName === this.username
                );
                finishVerification(!!isUserInGroup);
              },
              error: () => {
                finishVerification(false);
              }
            });
          } else {
            finishVerification(false);
            return;
          }
          this.f["complaintId"].setValue(res?.data?.complaintId);
          this.f["complaintNo"].setValue(res?.data?.complaintNo);
          this.f["name"].setValue(
            res?.data?.complainantTitle + " " + res?.data?.complainantName
          );
          this.f["cnic"].setValue(res?.data?.cnic);
          this.f["cellPhoneNo"].setValue(res?.data?.cellPhoneNo);
          this.f["passportNo"].setValue(res?.data?.passportNo);
          this.f["landLinePhoneNo"].setValue(res?.data?.landLinePhoneNo);
          this.f["address"].setValue(res?.data?.address);
          this.f["complaintCategoryName"].setValue(
            res?.data?.complaintCategoryName
          );
          this.f["complaintSubcategoryName"].setValue(
            res?.data?.complaintSubcategoryName
          );
          this.f["divisionName"].setValue(res?.data?.divisionName);
          this.f["districtName"].setValue(res?.data?.districtName);
          this.f["tehsilName"].setValue(res?.data?.tehsilName);
          this.f["tehsilId"].setValue(res?.data?.tehsilId);
          this.f["mouzaId"].setValue(res?.data?.mouzaId);
          this.f["mouzaName"].setValue(res?.data?.mouzaName);
          this.f["daysRequired"].setValue(res?.data?.daysRequired);
          this.f["workCode"].setValue(res?.data?.workCode);
          this.f["complaintSource"].setValue(res?.data?.complaintSource);
          this.f["legalFramework"].setValue(res?.data?.legalFramework);
          this.f["helplineIdentifier"].setValue(res?.data?.helplineIdentifier);
          this.f["assignTo"].setValue(res?.data?.assignTo);
          this.f["complaintDocument"].setValue(res?.data?.complaintDocument);
          // this.f['attachments'].setValue(this.createAttachmentForm()),
          //  this.f["description"].setValue(res?.data?.description);
          this.f["complaintDetails"].setValue(res?.data?.complaintDetails);

          const descriptionControl = this.f["complaintDetails"];
          if (!descriptionControl.value) {
            descriptionControl.setValue(res?.data?.complaintDetails);
          }
          this.f["priority"].setValue(res?.data?.priority);
          this.f["severity"].setValue(res?.data?.severity);
          this.f["gender"].setValue(res?.data?.gender);
          this.f["email"].setValue(res?.data?.email);
          this.f["assignToGroup"].setValue(res?.data?.assignToGroup);
          this.f["complaintAddress"].setValue(res?.data?.complaintAddress);
          this.f["currentProcessId"].setValue(
            res?.data?.currentProcess?.complaintProcessId
          );
          this.f["currentProcessName"].setValue(
            res?.data?.currentProcess?.name
          );
          this.f["isAnonymous"].setValue(res?.data?.isAnonymous);
          this.f["expectedResolutionDate"].setValue(
            res?.data?.expectedResolutionDate
          );
          this.f["segmentId"].setValue(res?.data?.segmentId);
          this.f["otherForum"].setValue(res?.data?.otherForum);
          this.f["otherSource"].setValue(res?.data?.otherSource);

          this.documentList = res?.data?.complaintDocument;
          this.f["complaintDocumentIds"].setValue(
            this.getDocumentIds(this.documentList)
          );

          if (
            ["closed", "appeal closed", "drop"].includes(
              this.f["currentProcessName"].value?.toLowerCase()
            )
          ) {
            this.f["currentProcessId"].disable();
            this.f["assignTo"].disable();
            this.f["assignToGroup"].disable();
            this.f["comment"].disable();
          }

          this.complaint?.currentProcess?.successorProcessId?.forEach(
            (element: any) => {
              let exist = this.complaintProcessList.filter(
                (e) => e.complaintProcessId == element
              )[0];
              if (exist) {
                this.complaintProcessSuccessorList.push(exist);
              }
            }
          );

          this.regionObject.divisionsId = res?.data?.divisionId;
          this.regionObject.districtsId = res?.data?.districtId;
          this.regionObject.tehsilId = res?.data?.tehsilId;
          this.regionObject.mouzaId = res?.data?.mouzaId;
          this.appDataService
            .getAllInternalUsersByRegion(this.regionObject)
            .subscribe({
              next: (res: any) => {
                this.usersListOriginal = res?.data;
                this.usersList = this.deepCopy(this.usersListOriginal);
              },
            });
          this.appDataService
            .getAllGroupsByRegion(this.regionObject)
            .subscribe({
              next: (res: any) => {
                this.groupsList = res?.data?.filter((g: any) => g.groupType === 'Committee') || [];
              },
            });
        },
        error: () => {
          const authStartTime = Date.now();
          const remaining = Math.max(0, 2000 - (Date.now() - authStartTime));
          setTimeout(() => {
            //this.openAlert('You are not authorized to view this complaint.');
            this.router.navigate(['/complaint']);
          }, remaining);
        }
      });
      this.appDataService.getTrckingByComplaintId(this.complaintId).subscribe({
        next: (res: any) => {
          this.trackingList = res?.data;
        },
      });
    }

    this.getMouza();

    this.f["mouzaId"].valueChanges.subscribe((value) => {
      const mouaza = this.mouzaList?.find((x) => x.adminStrId === value);
      if (mouaza) {
        this.f["mouzaName"].setValue(mouaza?.name);
      }
    });

    this.f["segmentId"].valueChanges.subscribe((value) => {
      this.usersList = [
        ...this.usersListOriginal.filter((user) => user?.segmentId == value),
      ];
    });

  }

  ngOnDestroy(): void {
    this.spinnerService.unsuppress();
    if (this.editor) {
      this.editor.destroy();
    }
    if (this.commentsEditor) {
      this.commentsEditor.destroy();
    }
  }

  loadUserid(): void {
    const permissions = localStorage.getItem("permissions");
    if (permissions) {
      try {
        const parsedPermissions = JSON.parse(permissions);
        const getUserId = parsedPermissions.USER;
        const getUserRole = parsedPermissions.role;
        this.userRole = getUserRole;
        if (getUserId) {
          this.userId = getUserId;
        }
      } catch (error) {
        console.error("Error parsing localStorage data:", error);
      }
    }
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      complaintId: [null],
      complaintNo: [""],
      complainantName: [{ value: "", disabled: true }],
      name: [{ value: "", disabled: true }],
      cnic: [{ value: "", disabled: true }],
      cellPhoneNo: [{ value: "", disabled: true }],
      passportNo: [{ value: "", disabled: true }],
      landLinePhoneNo: [{ value: "", disabled: true }],
      address: [{ value: "", disabled: true }],
      gender: [{ value: "", disabled: true }],
      email: [{ value: "", disabled: true }],
      complaintCategoryName: [{ value: "", disabled: true }],
      complaintSubcategoryName: [{ value: "", disabled: true }],
      divisionName: [{ value: "", disabled: true }],
      districtName: [{ value: "", disabled: true }],
      tehsilName: [{ value: "", disabled: true }],
      mouzaName: [{ value: "" }],
      mouzaId: [""],
      complaintAddress: [{ value: "", disabled: true }],
      currentProcessId: [""],
      currentProcessName: [""],
      daysRequired: [{ value: "", disabled: true }],
      assignTo: [""],
      complaintDetails: [{ value: "", disabled: true }],
      priority: [""],
      severity: [""],
      assignToGroup: [""],
      workCode: [{ value: "", disabled: true }],
      complaintSource: [{ value: "", disabled: true }],
      legalFramework: [{ value: "", disabled: true }],
      helplineIdentifier: [{ value: "", disabled: true }],
      //description: [{ value: "", disabled: true }],
      comment: [""],
      complaintDocument: [""],
      complaintDocumentIds: [null],
      attachments: this.formBuilder.array([]),
      department: [""],
      complaintCategoryId: [""],
      complaintSubcategoryId: [""],
      divisionId: [""],
      districtId: [""],
      tehsilId: [""],
      isAnonymous: [{ value: false, disabled: true }],
      expectedResolutionDate: [{ value: "", disabled: true }],
      segmentId: [""],
      otherForum: [{ value: "", disabled: true }, [Validators.maxLength(200)]],
      otherSource: [{ value: "", disabled: true }],
    }, { validators: complaintStatusValidator });
  }

  getAllWorkFlowStepsBySegment(id: number): void {
    this.appDataService.getAllWorkFlowStepsBySegment(id).subscribe({
      next: (res: any) => {
        this.complaintProcessList = res?.data || [];
      },
    });
  }
  getPersonalInfo(id: any): void {
    this.appDataService.getPersonalInfo(id).subscribe({
      next: (res: any) => {
        this.f["name"].setValue(res?.data?.complainantName);
        this.f["cnic"].setValue(res?.data?.cnic);
        this.f["cellPhoneNo"].setValue(res?.data?.cellPhoneNo);
        this.f["passportNo"].setValue(res?.data?.passportNo);
        this.f["gender"].setValue(res?.data?.gender);
        this.f["email"].setValue(res?.data?.email);
        this.f["address"].setValue(res?.data?.address);
      }
    });
  }


  getMouza(): void {
    const headers = new HttpHeaders().set('X-Skip-Spinner', 'true');
    const url = AppUrlConstants.host + AppUrlConstants.ADMINISTRATIVE_STRUCTURE_URL + AppUrlConstants.ADMIN_STRUCTURE_BY_IDS;

    this.isMouzaLoading = true;
    this.http.post<any>(url, this.mouzaIds, { headers }).subscribe({
      next: (res: any) => {
        this.allMouzaList = res?.data || [];
        this.mouzaList = this.allMouzaList.filter(
          (d: any) => d?.parentAdminStrId == this.f["tehsilId"].value
        );
        this.originalMouzaList = [...this.mouzaList];
        this.isMouzaLoading = false;
      },
      error: () => {
        this.isMouzaLoading = false;
      }
    });
  }

  private getDocumentIds(list: any[]): number[] {
    const ids: number[] = [];
    list?.forEach((item: any) => {
      if (item?.complaintDocumentId) {
        ids.push(item.complaintDocumentId);
      }
    });
    return ids;
  }

  deepCopy(data: any): any {
    return JSON.parse(JSON.stringify(data));
  }

  downloadPdf(_pdfName?: string): void {
    this.pdfService.generate(this.complaint, this.trackingList, this.canSeeInfo);
  }

  // Updated formatDate method with better type safety
  formatDate(
    date: string | Date | null | undefined,
    format: string = "yyyy-MM-dd"
  ): string {
    if (!date) return "";

    try {
      const dateObj: Date = new Date(date);

      // Check if date is valid
      if (isNaN(dateObj.getTime())) return "";

      if (format === "dd MMM yyyy hh:mm a") {
        const day: string = dateObj.getDate().toString().padStart(2, "0");
        const month: string = dateObj.toLocaleString("en", { month: "short" });
        const year: number = dateObj.getFullYear();
        let hours: number = dateObj.getHours();
        const minutes: string = dateObj
          .getMinutes()
          .toString()
          .padStart(2, "0");
        const ampm: string = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'

        return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
      }

      return dateObj.toISOString().split("T")[0]; // Simple format: YYYY-MM-DD
    } catch (e) {
      console.error("Error formatting date:", e);
      return "";
    }
  }

  removeAttachment(index: number, id: number): void {
    this.documentList.splice(index, 1);
    this.f["complaintDocumentIds"].setValue(
      this.getDocumentIds(this.documentList)
    );
    this.appDataService.deleteDocumentService(id).subscribe();
  }

  updateIsPublished(document: any): void {
    const data = {
      complaintDocumentId: document?.complaintDocumentId,
      documentName: document?.documentName,
      documentType: document?.documentType,
      isPublished: document?.isPublished,
    };

    this.appDataService.publishDocument(data).subscribe((res: any) => { });
  }
  confirmSave(): void {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        title: 'SAVE_CONFIRM_TITLE',
        message: 'SAVE_CONFIRM_MESSAGE',
        buttonText: { cancel: 'NO', confirm: 'YES' },
        showConfirm: true,
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) this.update();
    });
  }

  confirmCancel(): void {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        title: 'CANCEL_CONFIRM_TITLE',
        message: 'CANCEL_CONFIRM_MESSAGE',
        buttonText: { cancel: 'NO', confirm: 'YES' },
        showConfirm: true,
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) this.router.navigate(['/complaint']);
    });
  }

  update(): void {
    this.appDataService.updateComplaint(this.form.value).subscribe({
      next: (res: any) => {
        this.openSuccessAlert('Complaint updated Successfully!');
      },
      error: (e: any) => {
        if (e?.status == 409) {
          if (e?.error?.errors !== null) {
            for (const property in e.error.errors) {
              this.openAlert(e.error.errors[property]);
            }
          } else {
            this.openAlert(e?.error?.message);
          }
        }
      },
    });
  }

  openSuccessAlert(message: string): void {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: { title: 'SUCCESS', message, showConfirm: false, hideActions: true },
    });
    setTimeout(() => {
      dialogRef.close();
      window.location.reload();
    }, 2000);
  }
  maskPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber || phoneNumber.length < 4) return "****";

    // Format as +92XXXXXXXXXX
    let formattedNumber = phoneNumber;
    if (phoneNumber.startsWith("03")) {
      formattedNumber = "+92" + phoneNumber.substring(1);
    }

    const visiblePart = formattedNumber.substring(0, 5); // Show +9234
    const maskedPart = "*".repeat(formattedNumber.length - 5);

    return `${visiblePart}${maskedPart}`;
  }

  maskEmail(email: string): string {
    if (!email || email.length < 4) return "***";

    const atIndex = email.indexOf("@");
    if (atIndex === -1) return "Invalid email";

    const localPart = email.substring(0, atIndex);
    const domain = email.substring(atIndex);

    const visiblePart = localPart.slice(0, 4);
    const maskedPart = "*".repeat(localPart.length - 1);

    return `${visiblePart}${maskedPart}`;
  }

  sendSms(): void {
    let sendMessageDto: SendMessageModel = {
      title: "SEND_SMS",
      sendToLabel: "PHONE_NUMBER",
      sendTo: this.maskPhoneNumber(this.form.getRawValue()?.cellPhoneNo) ?? "",
      message: "",
      complaintId: this.form.value?.complaintId ?? "",
    };
    const dialogRef = this.dialog.open(SendSmsDialogComponent, {
      data: sendMessageDto,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      // console.log(result);
    });
  }

  sendEmail(): void {
    let sendMessageDto: SendMessageModel = {
      title: "SEND_EMAIL",
      sendToLabel: "EMAIL",
      sendTo: this.maskEmail(this.form.getRawValue()?.email) ?? "",
      message: "",
      complaintId: this.form.value?.complaintId ?? "",
    };
    const dialogRef = this.dialog.open(SendEmailDialogComponent, {
      data: sendMessageDto,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      // console.log(result);
    });
  }

  openAlert(message: string): void {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        message: message,
        buttonText: {
          cancel: "OK",
        },
      },
    });
  }

  onSelectFile(event: any): void {
    this.fileToUpload = event.target.files.item(0);
    if (this.fileToUpload) {
      // Check file type and size
      const allowedTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "image/gif",
        "application/pdf",
      ];
      const maxSize = 5 * 1024 * 1024;
      if (
        allowedTypes.includes(this.fileToUpload.type) &&
        this.fileToUpload.size <= maxSize
      ) {
        this.fileError = false;
        const formdata: FormData = new FormData();
        formdata.append("isPublished", "true"); // Append isPublished key
        formdata.append("file", this.fileToUpload);
        formdata.append("complaintId", this.f["complaintId"].value);
        this.appDataService.updateComplaintDocumentService(formdata).subscribe({
          next: (res: any) => {
            this.documentList.push(res?.data);
            this.f["complaintDocumentIds"].setValue(
              this.getDocumentIds(this.documentList)
            );
          },
        });
      } else {
        this.fileError = true;
      }
      event.target.value = null;
    }
  }

  // download(id: number): void {
  //   this.crudService
  //     .getById(
  //       id,
  //       "COMPLAINT_URL" + "DOCUMENT_DOWNLOAD" // Replace with actual constants
  //     )
  //     .subscribe({
  //       next: (res: any) => {
  //         var link = document.createElement("a");
  //         link.href =
  //           "data:" +
  //           res?.data?.documentType +
  //           ";base64," +
  //           res?.data?.complaintDocument;
  //         link.download = res?.data?.documentName;
  //         document.body.appendChild(link);
  //         link.click();
  //         document.body.removeChild(link);
  //       },
  //       error: (e: any) => {
  //         if (e?.status == 409) {
  //           if (e?.error?.errors !== null) {
  //             for (const property in e.error.errors) {
  //               this.openAlert(e.error.errors[property]);
  //             }
  //           } else {
  //             this.openAlert(e?.error?.message);
  //           }
  //         }
  //       },
  //     });
  // }

  preview(id: number): void {
    const doc = this.documentList.find(d => d.complaintDocumentId === id);
    if (!doc || !doc.complaintDocument) {
      this.openAlert("Document not found or invalid.");
      return;
    }
    this.dialog.open(AttachmentPreviewDialogComponent, {
      data: {
        documentName: doc.documentName,
        documentType: doc.documentType,
        complaintDocument: doc.complaintDocument,
      },
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'fullscreen-preview-dialog',
    });
  }

  download(id: number): void {
    const doc = this.documentList.find(d => d.complaintDocumentId === id);
    if (!doc || !doc.complaintDocument) {
      this.openAlert("Document not found or invalid.");
      return;
    }

    const byteCharacters = atob(doc.complaintDocument);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: doc.documentType });

    const link = document.createElement("a");
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.download = doc.documentName || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }


  openDropModal(): void {
    const dialogRef = this.dialog.open(DropDialogComponent, {
      data: {
        complaintId: this.form.value?.complaintId,
      },
    });
  }

  openAppealModal(): void {
    const dialogRef = this.dialog.open(AppealDialogComponent, {
      data: {
        complaintId: this.form.value?.complaintId,
      },
    });
  }

  openReopenModal(): void {
    const dialogRef = this.dialog.open(ReopenDialogComponent, {
      data: {
        complaintId: this.form.value?.complaintId,
        complaintProcessList: this.complaintProcessList,
      },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.submitted) {
        window.location.reload();
      }
    });
  }

  private readonly INVALID_MOBILES = ['03000000000', '+923000000000'];
  private readonly INVALID_EMAILS = ['anonymous@pulse.gop.pk', 'anonymous@pulse.com'];

  isValidMobile(mobile: string | undefined | null): boolean {
    if (!mobile || mobile.trim() === '') return false;
    return !this.INVALID_MOBILES.includes(mobile.trim());
  }

  isValidEmail(email: string | undefined | null): boolean {
    if (!email || email.trim() === '') return false;
    return !this.INVALID_EMAILS.includes(email.trim().toLowerCase());
  }

  // Check if complaint status is closed/appeal closed/drop
  isComplaintClosed(): boolean {
    return ['closed', 'appeal closed', 'drop'].includes(
      this.complaint?.currentProcess?.name?.toLowerCase() || ''
    );
  }

  // Check if attachment functionality should be disabled
  isAttachmentDisabled(): boolean {
    return this.isComplaintClosed();
  }

  // Check if special GRM Specialist button should be visible
  showGrmSpecialistButton(): boolean {
    return this.username === 'GRM-Specialist' &&
      this.complaint?.currentProcess?.name?.toLowerCase() === 'drop' &&
      this.complaint?.segmentId === 1;
  }

  truncateText(text: string, charLimit: number): string {
    if (!text) return ""; // Handle null or undefined values gracefully
    return text.length > charLimit
      ? text.substring(0, charLimit) + "..."
      : text;
  }

  filterMouza(event: any): void {
    const searchTerm = event.target.value.toLowerCase();
    // Filter in-place using this.mouzaList
    if (searchTerm) {
      this.mouzaList = this.mouzaList.filter((mouza) =>
        mouza.name.toLowerCase().includes(searchTerm)
      );
    } else {
      this.mouzaList = this.originalMouzaList; // Reset to original data
    }
  }

  clearMouzaSearch(): void {
    if (this.searchMouza?.nativeElement) {
      this.searchMouza.nativeElement.value = "";
      this.mouzaList = this.originalMouzaList;
    }
  }
}
